import { Dispatch, FormEvent, SetStateAction, useEffect, useMemo, useState } from 'react';
import { Modal } from 'src/components/common';
import { toggleTouchedFields } from 'src/utils';
import { useGetPlaces, useUpdateFormValue, useValidateErrors } from 'src/hooks';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import InputField from 'src/components/common/formFields/inputField/InputField';
import { componentSizeVariantsEnum } from 'src/constants/common.constants';
import Button from 'src/components/button/Button';
import Typography from 'src/components/common/typography/Typography';
import TextAreaField from 'src/components/common/formFields/textAreaField/TextAreaField';
import SelectField from 'src/components/common/formFields/selectField/SelectField';
import { TSelectCardRadioButton } from 'src/components/common/formFields/selectCardRadio/SelectCardRadio';
import { Role } from 'src/constants/user.constants';
import { editProfile } from 'src/store/profile/profile.slice';
// import { Country, State, City } from "country-state-city";

import './editProfile.scss';
import { editProfileFormFields, genderOptions, editProfileErrors } from './editProfile.constants';
import editProfileValidationSchema from './editProfile.validationSchema';
import SuggestionSelectField from 'src/components/common/formFields/suggestionSelectField/suggestionSelectField';
import PhoneNumberBox from 'src/components/common/formFields/PhoneNumberBox/PhoneNumberBox';
import axios from 'axios';

export type TEditProfile = {
  isModalOpen: boolean; // whether the modal is open
  setIsModalOpen: Dispatch<SetStateAction<boolean>>; // function to run when the modal is closed
};
export type TSelectOptions = {
  label: string,
  value: string
}
export type TEditProfileValues = {
  id: string;
  name: string;
  role: string;
  introduction: string;
  about: string;
  gender: string;
  address: string;
  country: string;
  state: string;
  city: string;
  cityQuery?: string;
  zipCode: string;
  phoneNumber: string;
  linkedin?: string;
  facebook?: string;
  twitter?: string;
  instagram?: string;
};

const allFieldsUntouched = toggleTouchedFields({ fields: editProfileFormFields, isTouched: false });
const allFieldsTouched = toggleTouchedFields({ fields: editProfileFormFields, isTouched: true });
const username = 'htraction';
const EditProfile = ({ isModalOpen, setIsModalOpen }: TEditProfile) => {
  const [formValues, setFormValues] = useState<TEditProfileValues>(editProfileFormFields);
  const [touchedFormFields, setTouchedFormFields] =
    useState<Record<keyof TEditProfileValues, boolean>>(allFieldsUntouched);
  const [errors, setErrors] = useState<Record<keyof TEditProfileValues, string>>(editProfileErrors);

  // Hooks
  const { updateFormValue } = useUpdateFormValue();
  const { validateErrors } = useValidateErrors();

  const { profile } = useAppSelector((store) => store.profile);
  const { loading } = useAppSelector((store: any) => store.common);
  const dispatch = useAppDispatch();

  const updateForm = ({ fieldName, value }: { fieldName: string; value: string }) => {
    const setFormValue = setFormValues as Dispatch<SetStateAction<Record<string, string | number>>>;
    updateFormValue({ fieldName, value, setFormValue });
  };
  // state
  const [countryOptions, setCountryOptions] = useState<TSelectOptions[]>([])
  const [states, setStates] = useState<TSelectOptions[]>([])
  const [cityOptions, setCityOptions] = useState<TSelectOptions[]>([])

  function handleCountryChange(data: TSelectOptions) {
    // Fetch states for the selected country
    if (data?.value) {
      getStates(data?.value).then(stateList => {
        setStates(stateList);
      });
    }
    setCityOptions([]);
  }
  // // Handle state selection
  const handleStateChange = (selectedOption: TSelectOptions, values) => {
    if (selectedOption?.value) {
      const selectedCountry: any = countryOptions?.find((item) => item.label === values?.country);
      getCities(selectedOption.value, selectedCountry?.countryCode).then(cities => {
        const modifiedCities = cities?.map((item: any) => ({ ...item, label: item?.name, value: item?.name }))
        setCityOptions(modifiedCities);
      });
    }
  };
  // // Handle city selection
  const handleCityChange = (selectedOption: TSelectOptions) => {
    updateForm({ fieldName: 'city', value: selectedOption?.label || '' })
  };

  const validateForm = async () => {
    return await validateErrors({
      validationSchema: editProfileValidationSchema,
      formValues,
      errorsInitialState: editProfileErrors,
      setErrors
    });
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setTouchedFormFields(allFieldsTouched);

    const validationErrors = await validateForm();

    if (validationErrors?.length) {
      return validateErrors;
    }

    // edit profile api call
    dispatch(
      editProfile({
        id: profile.id,
        name: formValues.name,
        role: formValues.role,
        introduction: formValues.introduction || '',
        about: formValues.about || '',
        address: formValues.address || '',
        country: formValues.country || '',
        state: formValues.state || '',
        city: formValues.city || '',
        zipCode: formValues.zipCode || '',
        phoneNumber: formValues.phoneNumber || 0,
        linkedin: formValues?.linkedin || '',
        facebook: formValues?.facebook || '',
        instagram: formValues?.instagram || '',
        twitter: formValues?.twitter || '',
        ...(formValues.gender && { gender: formValues.gender })
      })
    );
  };

  const closeModal = () => {
    setErrors(editProfileErrors);
    setFormValues(editProfileFormFields);
    setTouchedFormFields(allFieldsUntouched);
    setIsModalOpen(false);
  };

  useEffect(() => {
    validateForm();
  }, [JSON.stringify(formValues)]);

  let roleOptions: TSelectCardRadioButton[] = [];

  if (
    profile.role === Role.MENTOR ||
    profile.role === Role.INVESTOR ||
    profile?.role === Role.MENTOR_INVESTOR
  ) {
    roleOptions = [
      ...roleOptions,
      {
        label: 'Investor',
        value: 'Investor',
        id: 'investor'
      },
      {
        label: 'Mentor',
        value: 'Mentor',
        id: 'mentor'
      },
      {
        label: 'Mentor & Investor',
        value: 'Mentor and Investor',
        id: 'Mentor-Investor'
      }
    ];
  }

  if (profile.role === Role.ENTREPRENEUR || profile.role === Role.ASPIRING_ENTREPRENEUR) {
    roleOptions = [
      ...roleOptions,
      {
        label: 'Aspiring Entrepreneur',
        value: 'Aspiring Entrepreneur',
        id: 'aspiring-entrepreneur'
      },
      { label: 'Entrepreneur', value: 'Entrepreneur', id: 'entrepreneur' }
    ];
  }

  if (profile.role === Role.SERVICE_PROVIDERS_STARTUP) {
    roleOptions = [
      ...roleOptions,
      {
        label: 'Aspiring Entrepreneur',
        value: 'Aspiring Entrepreneur',
        id: 'aspiring-entrepreneur'
      },
      { label: 'Entrepreneur', value: 'Entrepreneur', id: 'entrepreneur' },
      {
        label: 'Investor',
        value: 'Investor',
        id: 'investor'
      },
      {
        label: 'Mentor',
        value: 'Mentor',
        id: 'mentor'
      }
    ];
  }

  function getCountries() {
    axios.get(`https://secure.geonames.org/countryInfoJSON?username=${username}`)
      .then(response => {
        const countries = response.data.geonames?.map((item) => ({ ...item, label: item?.countryName, value: item?.geonameId }))
        setCountryOptions(countries);
      })
      .catch(error => console.error('Error fetching countries:', error));
  }
  async function getStates(geonameId: string): Promise<TSelectOptions[]> {
    try {
      const response = await axios.get(`https://secure.geonames.org/childrenJSON?geonameId=${geonameId}&username=${username}`);
      return response.data.geonames.map((item: any) => ({ label: item.name, value: item.adminCode1 }));
    } catch (error) {
      console.error('Error fetching states:', error);
      return [];
    }
  }
  async function getCities(adminCode1: string, countryCode: string): Promise<TSelectOptions[]> {
    try {
      const response = await axios.get(`https://secure.geonames.org/searchJSON?adminCode1=${adminCode1}&country=${countryCode}&featureClass=P&username=${username}`);
      return response.data.geonames
    } catch (error) {
      console.log("Error", error);
      return []
    }
  }
  useEffect(() => {
    // Fetch countries
    getCountries()
  }, []);

  useEffect(() => {
    // when edit profile modal is opened, fill the form with existing values.
    if (isModalOpen) {
      const currentValues: TEditProfileValues = {
        id: profile.id || '',
        name: profile.name || '',
        role: profile.role || '',
        introduction: profile.introduction || '',
        about: profile.about || '',
        gender: profile.gender || '',
        address: profile.address || '',
        country: profile.country || '',
        state: profile.state || '',
        city: profile.city || '',
        cityQuery: profile.city || '',
        zipCode: profile.zipCode || '',
        phoneNumber:
          profile.phoneNumber === '0' ? '' : profile?.phoneNumber ? profile?.phoneNumber : '',
        linkedin: profile?.linkedin || '',
        twitter: profile?.twitter || '',
        facebook: profile?.facebook || '',
        instagram: profile?.instagram || ''
      };

      if (profile?.country) {
        const selectedCountry: any = countryOptions?.find((item) => item.label === profile?.country);
        if (selectedCountry) {
          getStates(selectedCountry?.value).then(stateList => {
            setStates(stateList);
            const selectedState = stateList?.find((item) => item?.label === profile?.state);

            if (selectedState) {
              getCities(selectedState.value, selectedCountry?.countryCode).then(cities => {
                const modifiedCities = cities?.map((item: any) => ({ ...item, label: item?.name, value: item?.name }))
                setCityOptions(modifiedCities);
              });
            }
          });
        }
      }
      setFormValues(currentValues);
    }
    }, [isModalOpen, countryOptions]);

  return (
    <Modal
      isModalOpen={isModalOpen}
      onModalClose={closeModal}
      title="Edit Profile"
      className="edit-profile-modal"
    >
      <div className="edit-profile">
        <form onSubmit={handleSubmit} className="edit-profile__form">
          <div className="edit-profile__form__fields">
            {/*
             ** Name
             */}
            <InputField
              label="Your name"
              id="name"
              variant={componentSizeVariantsEnum.SMALL}
              placeholder="My Name"
              type="text"
              value={formValues.name}
              onChange={(e) => {
                updateForm({ fieldName: 'name', value: e.target.value });
              }}
              errorMessage={errors.name}
              isTouched={touchedFormFields.name}
              required
            />
            {/*
             ** Role
             */}
            <SelectField
              label="I am:"
              placeholder="Select your role"
              variant={componentSizeVariantsEnum.SMALL}
              selectedValue={formValues.role}
              options={roleOptions}
              onSelection={({ value }) => {
                updateForm({ fieldName: 'role', value: value as unknown as string });
              }}
              required
            />
            {/*
             ** Introduction
             */}
            <InputField
              label={
                <Typography as="p" variant="caption" fontWeight="semiBold">
                  Quick Introduction{' '}
                </Typography>
              }
              id="introduction"
              variant={componentSizeVariantsEnum.SMALL}
              placeholder="My introduction"
              type="text"
              value={formValues.introduction}
              onChange={(e) => {
                updateForm({ fieldName: 'introduction', value: e.target.value });
              }}
              isTouched={touchedFormFields.introduction}
              errorMessage={errors.introduction}
            />

            {/*
             ** About me
             */}
            <TextAreaField
              label="About me"
              id="about"
              variant={componentSizeVariantsEnum.SMALL}
              value={formValues.about}
              onChange={(e) => {
                updateForm({ fieldName: 'about', value: e.target.value });
              }}
              errorMessage={errors.about}
            />
            {/*
             ** Grid of four fields -> phone number, email address, gender, and dob
             */}
            <div className="edit-profile__form__fields__grid">
              {/*
               ** Phone number
               */}
              <PhoneNumberBox
                value={formValues.phoneNumber || ''}
                label="Contact number" onChange={(e) => updateForm({ fieldName: 'phoneNumber', value: e.target.value })}
                isTouched={touchedFormFields.phoneNumber}
                errorMessage={errors.phoneNumber}
              />

              {/* <InputField
                label={
                  <Typography as="p" variant="caption" fontWeight="semiBold">
                    Contact number
                  </Typography>
                }
                id="phoneNumber"
                variant={componentSizeVariantsEnum.SMALL}
                placeholder="Contact number"
                type="string"
                value={formValues.phoneNumber || ''}
                onChange={(e) => {
                  updateForm({ fieldName: 'phoneNumber', value: e.target.value });
                }}
                isTouched={touchedFormFields.phoneNumber}
                errorMessage={errors.phoneNumber}
              /> */}
              {/*
               ** Email Address
               */}
              <div className="edit-profile__form__fields__grid__field">
                <Typography as="p" variant="caption" fontWeight="semiBold">
                  Email Address
                </Typography>
                <Typography as="p" variant="caption" fontWeight="regular">
                  {profile.email}
                </Typography>
              </div>
              {/*
               ** Gender
               */}
              <SelectField
                label="Your Gender"
                placeholder="Select your Gender"
                variant={componentSizeVariantsEnum.SMALL}
                selectedValue={formValues.gender}
                options={genderOptions}
                onSelection={({ value }) => {
                  updateForm({ fieldName: 'gender', value: value as unknown as string });
                }}
              />{' '}
              {/*
               ** Linkedin
               */}
              <InputField
                label={
                  <Typography as="p" variant="caption" fontWeight="semiBold">
                    Your LinkedIn profile URL
                  </Typography>
                }
                id="linkedin"
                variant={componentSizeVariantsEnum.SMALL}
                placeholder="LinkedIn profile URL"
                type="string"
                value={formValues.linkedin || ''}
                onChange={(e) => {
                  updateForm({ fieldName: 'linkedin', value: e.target.value });
                }}
                isTouched={touchedFormFields.linkedin}
                errorMessage={errors.linkedin}
              />
              <InputField
                label={
                  <Typography as="p" variant="caption" fontWeight="semiBold">
                    Your X profile URL
                  </Typography>
                }
                id="twitter"
                variant={componentSizeVariantsEnum.SMALL}
                placeholder="X profile URL"
                type="string"
                value={formValues.twitter || ''}
                onChange={(e) => {
                  updateForm({ fieldName: 'twitter', value: e.target.value });
                }}
                isTouched={touchedFormFields.twitter}
                errorMessage={errors.twitter}
              />
              <InputField
                label={
                  <Typography as="p" variant="caption" fontWeight="semiBold">
                    Your Instagram profile URL
                  </Typography>
                }
                id="instagram"
                variant={componentSizeVariantsEnum.SMALL}
                placeholder="Instagram profile URL"
                type="string"
                value={formValues.instagram || ''}
                onChange={(e) => {
                  updateForm({ fieldName: 'instagram', value: e.target.value });
                }}
                isTouched={touchedFormFields.instagram}
                errorMessage={errors.instagram}
              />
              <InputField
                label={
                  <Typography as="p" variant="caption" fontWeight="semiBold">
                    Your Facebook profile URL
                  </Typography>
                }
                id="facebook"
                variant={componentSizeVariantsEnum.SMALL}
                placeholder="Facebook profile URL"
                type="string"
                value={formValues.facebook || ''}
                onChange={(e) => {
                  updateForm({ fieldName: 'facebook', value: e.target.value });
                }}
                isTouched={touchedFormFields.facebook}
                errorMessage={errors.facebook}
              />
            </div>
            {/*
             ** Address fields
             */}
            <div className="edit-profile__form__fields__address">
              <Typography as="p" variant="body-2" fontWeight="semiBold">
                Address
              </Typography>
              {/* <InputField
                label="Address line"
                id="address"
                variant={componentSizeVariantsEnum.SMALL}
                placeholder="Address line"
                type="text"
                value={formValues.address}
                onChange={(e) => {
                  updateForm({ fieldName: 'address', value: e.target.value });
                }}
              /> */}
              <div className="edit-profile__form__fields__address__grid">

                <SuggestionSelectField
                  variant={componentSizeVariantsEnum.SMALL}
                  label="Country"
                  options={countryOptions}
                  value={formValues?.country || ''}
                  onChange={(data) => {
                    updateForm({ fieldName: 'country', value: data?.label || '' });
                    updateForm({ fieldName: 'state', value: '' });
                    updateForm({ fieldName: 'city', value: '' });
                    handleCountryChange(data)
                  }}
                  placeholder="Country"
                  isSearchable
                />

                <SuggestionSelectField
                  variant={componentSizeVariantsEnum.SMALL}
                  label="State"
                  options={states}
                  value={formValues?.state || ''}
                  onChange={(data) => {
                    handleStateChange(data, formValues);
                    updateForm({ fieldName: 'state', value: data?.label || '' });
                    updateForm({ fieldName: 'city', value: '' });
                  }}
                  placeholder="State"
                  isSearchable
                />

                <SuggestionSelectField
                  variant={componentSizeVariantsEnum.SMALL}
                  label="City"
                  options={cityOptions}
                  value={formValues?.city || ''}
                  onChange={handleCityChange}
                  placeholder="City"
                  isSearchable
                />
                <InputField
                  label="Zip Code"
                  id="zipCode"
                  variant={componentSizeVariantsEnum.SMALL}
                  placeholder="Zip Code"
                  type="text"
                  value={formValues.zipCode}
                  onChange={(e) => {
                    updateForm({ fieldName: 'zipCode', value: e.target.value });
                  }}
                />
              </div>
            </div>
          </div>
          <div className="edit-profile__form__buttons">
            <Button variant="secondary" type="button" size="small" onClick={closeModal}>
              Close
            </Button>

            <Button variant="primary" type="submit" size="small" loading={loading}>
              Save Changes
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default EditProfile;
